<script>
  // Who is on the clock right now.
  //
  // Two clocks run here. The board itself is refetched every POLL_MS (or when the
  // merchant presses Refresh), and between fetches the elapsed timers tick locally
  // once a second so the page feels live without a request behind every second.
  //
  // Elapsed is anchored to the server's clock, never the browser's: we keep the
  // server time from the last fetch and add the time since, so a laptop with a
  // wrong clock still shows the right numbers. Same reasoning as the mobile app.
  const POLL_MS = 30000
  const TICK_MS = 1000

  export default {
    props: {
      boardPath: { type: String, required: true },
      // Template for the per-person punch endpoint, with :id to substitute. Absent
      // when the viewer may look but not write.
      punchPathTemplate: { type: String, required: false, default: '' }
    },
    data() {
      return {
        board: null,
        loading: true,
        refreshing: false,
        failed: false,
        showOffDuty: false,
        serverTimeUtc: null,
        fetchedAt: null,
        now: Date.now(),
        // One key per click, held until that click succeeds, so a retry after a
        // flaky connection resolves to the same punch rather than a second one.
        punching: {},
        punchKeys: {}
      }
    },
    computed: {
      onTheClock() {
        return this.board ? this.board.entries.filter(entry => entry.on_the_clock) : []
      },
      // On the clock and plausibly still working. A stale entry is a forgotten
      // punch, so it stays in the list (flagged) but never counts towards the
      // headline number or the running total.
      working() {
        return this.onTheClock.filter(entry => !entry.stale)
      },
      offDuty() {
        return this.board ? this.board.entries.filter(entry => !entry.on_the_clock) : []
      },
      // Server time as of right now: the last known server instant plus however
      // long the browser has been sitting there since.
      effectiveNow() {
        if (!this.serverTimeUtc) return null
        return this.serverTimeUtc + (this.now - this.fetchedAt)
      },
      activeSeconds() {
        return this.working.reduce((total, entry) => total + this.elapsedSeconds(entry), 0)
      },
      updatedAgo() {
        if (!this.fetchedAt) return ''
        const seconds = Math.max(Math.round((this.now - this.fetchedAt) / 1000), 0)
        if (seconds < 5) return 'just now'
        if (seconds < 60) return `${seconds} seconds ago`
        const minutes = Math.round(seconds / 60)
        return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`
      }
    },
    created() {
      this.load()
      this.poller = setInterval(() => { if (!document.hidden) this.load() }, POLL_MS)
      this.ticker = setInterval(() => { this.now = Date.now() }, TICK_MS)
      this.onVisible = () => { if (!document.hidden) this.load() }
      document.addEventListener('visibilitychange', this.onVisible)
    },
    beforeDestroy() {
      clearInterval(this.poller)
      clearInterval(this.ticker)
      document.removeEventListener('visibilitychange', this.onVisible)
    },
    methods: {
      load() {
        this.refreshing = true
        this.$http.get(this.boardPath).then(response => {
          this.board = response.body
          this.serverTimeUtc = Date.parse(response.body.server_time_utc)
          this.fetchedAt = Date.now()
          this.now = Date.now()
          this.failed = false
        }, () => {
          this.failed = true
        }).finally(() => {
          this.loading = false
          this.refreshing = false
        })
      },
      elapsedSeconds(entry) {
        if (!entry.since_utc || !this.effectiveNow) return 0
        return Math.max(Math.round((this.effectiveNow - Date.parse(entry.since_utc)) / 1000), 0)
      },
      punchPath(entry) {
        return this.punchPathTemplate.replace(':id', entry.user_id)
      },
      punchKeyFor(entry) {
        if (!this.punchKeys[entry.user_id]) {
          this.$set(this.punchKeys, entry.user_id, this.uuid())
        }
        return this.punchKeys[entry.user_id]
      },
      // Clock someone in or out from the board -- a dead phone, one left in the van.
      punch(entry, type) {
        if (this.punching[entry.user_id]) return
        this.$set(this.punching, entry.user_id, true)

        this.$http.post(this.punchPath(entry), { type: type, form_key: this.punchKeyFor(entry) })
          .then(response => {
            this.board = response.body
            this.serverTimeUtc = Date.parse(response.body.server_time_utc)
            this.fetchedAt = Date.now()
            this.now = Date.now()
            // The click landed, so the next one is a new press and needs a new key.
            this.$delete(this.punchKeys, entry.user_id)
            this.notifySuccess(type === 'in' ? 'Clocked in.' : 'Clocked out.')
          }, error => {
            this.errorHandler(error)
          }).finally(() => {
            this.$delete(this.punching, entry.user_id)
          })
      },
      uuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
          const rand = Math.random() * 16 | 0
          return (char === 'x' ? rand : (rand & 0x3 | 0x8)).toString(16)
        })
      },
      duration(seconds) {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        if (hours === 0 && minutes === 0) return 'just started'
        if (hours === 0) return `${minutes}m`
        return `${hours}h ${minutes}m`
      },
      // One running shift, ticking. Only used per row: the headline tile sums
      // across everyone on the clock, so showing seconds there would move the
      // number by the number of open sessions each tick and read as a glitch
      // rather than as a live clock.
      //
      // Seconds are zero padded because the column would otherwise change width
      // every tick -- see font-variant-numeric on .ts-total for the other half.
      liveDuration(seconds) {
        if (seconds < 10) return 'just started'

        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const rest = seconds % 60
        // Not padStart: nothing else in the pack relies on an ES2017 polyfill.
        const padded = rest < 10 ? `0${rest}` : `${rest}`

        return hours === 0 ? `${minutes}m ${padded}s` : `${hours}h ${minutes}m ${padded}s`
      }
    }
  }
</script>

<template>
  <section class="ts-live">
    <div class="ts-section-head">
      <h5 class="bold-font">Right now</h5>
      <div class="ts-live__controls">
        <span v-if="!loading && !failed" class="grey-text ts-live__updated">Updated {{ updatedAgo }}</span>
        <span v-if="failed" class="ts-live__failed">Couldn't refresh. Trying again shortly.</span>
        <button type="button" class="btn btn-grey rounded-btn ts-live__refresh"
                :disabled="refreshing" @click="load">
          <i class="fas" :class="refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
          Refresh
        </button>
      </div>
    </div>

    <div v-if="loading" class="surface-card surface-card--pad center-align">
      <i class="fas fa-spinner fa-spin fa-2x grey-text"></i>
      <p class="grey-text">Loading who's on the clock...</p>
    </div>

    <template v-else-if="board">
      <div class="ts-tiles">
        <div class="surface-card surface-card--pad ts-tile">
          <div class="stat-number bold-font emerald">{{ working.length }}</div>
          <span class="grey-text">on the clock, of {{ board.roster_size }}</span>
        </div>
        <div class="surface-card surface-card--pad ts-tile">
          <div class="stat-number bold-font">{{ duration(activeSeconds) }}</div>
          <span class="grey-text">
            active time, across {{ working.length }}
            {{ working.length === 1 ? 'session' : 'sessions' }}
          </span>
        </div>
      </div>

      <div v-if="onTheClock.length === 0" class="card z-depth-0 ts-empty">
        <div class="empty-icon"><i class="fas fa-mug-hot"></i></div>
        <p>Nobody is on the clock right now.</p>
      </div>

      <div v-else class="card ts-card">
        <table class="highlight data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Status</th>
              <th>Since</th>
              <th class="right-align">Elapsed</th>
              <th class="right-align"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in onTheClock" :key="entry.user_id"
                class="clickable-row" :data-href="entry.path">
              <td>
                {{ entry.name }}
                <span v-if="entry.suspended" class="state-chip state-chip--inactive ts-name-chip">Suspended</span>
              </td>
              <td>
                <span v-if="entry.stale" class="state-chip state-chip--danger">Missing clock out</span>
                <span v-else class="state-chip state-chip--active">On the clock</span>
              </td>
              <td class="nowrap-cell">{{ entry.since_local }}</td>
              <td class="right-align ts-total">
                <span v-if="entry.stale" class="grey-text">Since {{ entry.since_local }}</span>
                <span v-else>{{ liveDuration(elapsedSeconds(entry)) }}</span>
              </td>
              <td class="right-align ts-row-actions">
                <button v-if="entry.can_punch" type="button" class="btn btn-grey rounded-btn btn-small"
                        :disabled="punching[entry.user_id]" @click="punch(entry, 'out')">
                  Clock out
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="offDuty.length" class="ts-live__off-duty">
        <button type="button" class="ts-live__toggle" @click="showOffDuty = !showOffDuty">
          <i class="fas fa-chevron-right ts-live__caret" :class="{ 'is-open': showOffDuty }"></i>
          {{ offDuty.length }} off duty
        </button>
        <ul v-if="showOffDuty" class="ts-live__off-duty-list">
          <li v-for="entry in offDuty" :key="entry.user_id" class="ts-person">
            <span class="ts-person__name" :title="entry.name">
              {{ entry.name }}
              <span v-if="entry.suspended" class="state-chip state-chip--inactive ts-name-chip">Suspended</span>
            </span>
            <button v-if="entry.can_punch" type="button" class="ts-person__action"
                    :disabled="punching[entry.user_id]" @click="punch(entry, 'in')">
              <i class="fas fa-play"></i> Clock in
            </button>
          </li>
        </ul>
      </div>
    </template>
  </section>
</template>
